// src/components/MessageBoard.js
import { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { db } from '../firebase/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  onSnapshot, 
  serverTimestamp 
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const MessageBoard = () => {
  const { userDetails, currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Set up real-time listener for messages
  useEffect(() => {
    if (!userDetails?.communityId) return;

    // Create query
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('communityId', '==', userDetails.communityId),
      orderBy('timestamp', 'asc')  // Add ordering
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const messageData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        }));

        setMessages(messageData);
        setLoading(false);
        
        // Scroll to bottom when new messages arrive
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      },
      (error) => {
        console.error('Error listening to messages:', error);
        setError('Error loading messages');
        setLoading(false);
      }
    );

    // Clean up listener on unmount
    return () => unsubscribe();
  }, [userDetails?.communityId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !userDetails?.communityId) return;

    setSending(true);
    try {
      const messageData = {
        text: newMessage.trim(),
        userId: currentUser.uid,
        userName: userDetails.name,
        userType: userDetails.userType,
        communityId: userDetails.communityId,
        timestamp: serverTimestamp()
      };

      await addDoc(collection(db, 'messages'), messageData);
      setNewMessage('');
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Error:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    return timestamp.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Log currentUser for debugging
  useEffect(() => {
    console.log("Current User:", currentUser);
  }, [currentUser]);

  return (
    <Card className="mt-4">
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{userDetails?.workspaceName} Team Message Board</h5>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div 
          className="message-container mb-3" 
          style={{ 
            maxHeight: '300px', 
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px'
          }}
        >
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted py-3">
              No messages yet. Be the first to write something!
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`mb-2 ${message.userId === currentUser?.uid ? 'text-end' : ''}`}
              >
                <div
                  className={`d-inline-block p-2 rounded ${
                    message.userId === currentUser?.uid 
                      ? 'bg-primary text-white' 
                      : 'bg-light'
                  }`}
                  style={{ maxWidth: '75%', wordBreak: 'break-word' }}
                >
                  <div className="small fw-bold">
                    {message.userName}
                    <Badge 
                      bg={message.userType === 'employer' ? 'danger' : 'info'}
                      className="ms-2"
                    >
                      {message.userType === 'employer' ? 'Admin' : 'Employee'}
                    </Badge>
                  </div>
                  <div>{message.text}</div>
                  <div className="small text-end opacity-75">
                    {formatTimestamp(message.timestamp)}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={currentUser ? "Type your message..." : "Please log in to send messages"}
              disabled={sending || !currentUser}
            />
            <Button 
              type="submit" 
              variant="primary" 
              disabled={sending || !newMessage.trim() || !currentUser}
            >
              {sending ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Send'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default MessageBoard;
